import { BreakpointObserver } from '@angular/cdk/layout'
import { CommonModule } from '@angular/common'
import { RouterModule } from '@angular/router'
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Title } from '@angular/platform-browser'

import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { MatTooltip } from '@angular/material/tooltip'
import { MatSnackBar } from '@angular/material/snack-bar'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { AdminOrg } from '../../../services/user/user.mapping'
import {
  EMPTY,
  Subscription,
  catchError,
  filter,
  lastValueFrom,
  of,
  switchMap,
  tap,
  timer,
} from 'rxjs'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { captureException } from '../../../util/captureException'

import { RenameBusinessMeetingComponent } from './rename-business-meeting/rename-business-meeting.component'
import { FileInformationInputComponent } from './file-information-input/file-information-input.component'
import { FileInformationViewComponent } from './file-information-view/file-information-view.component'
import { AssessmentStatus } from '../../../../_generated/graphql'
import { AvatarComponent } from '../../../components/avatar/avatar.component'
import { AiBadgeComponent } from '../../../components/ai-badge/ai-badge.component'
import { ButtonComponent } from '../../../components/button/button.component'
import { LoadingComponent } from '../../../components/loading/loading.component'
import {
  DialogResult,
  MessageDialogService,
} from '../../../components/message-dialog/message-dialog.service'
import {
  Option,
  SelectComponent,
} from '../../../components/select/select.component'
import {
  Assessment,
  KeyActionEvaluation,
  RoleModelAssessment,
  SkillMap,
} from '../../../services/assessment/assessment.mapping'
import { AuthService } from '../../../services/auth.service'
import { User } from '../../../services/user/user.mapping'
import { AssessmentStore } from '../../../stores/assessment.store'
import { UserStore } from '../../../stores/user.store'
import { isAudioFilePath } from '../../../util/file/audio'
import { getMimeType } from '../../../util/file/encoding'
import { StorageService } from '../../../services/storage.service'
import { getTitle } from '../../../util/accessibility'
import { CsvExportService } from '../../../services/csv-export/csv-export.service'
import { downloadBlob } from '../../../util/blob-download'
import {
  buildTranscriptFilename,
  canDownloadTranscript,
  formatTranscriptAsText,
} from '../../../util/format-transcript'
import {
  ContextMenu,
  ContextMenuComponent,
} from '../../../components/context-menu/context-menu.component'
import {
  CustomLabelValueMap,
  CustomLabelService,
  LABEL_KEYS,
  extractAssessmentLabels,
} from '../../../services/custom-label/custom-label.service'
import { SpeakerLabelPipe } from '../../../services/custom-label/speaker-label.pipe'
import { MarkdownPipe } from './markdown.pipe'
import { AssessmentMobileComponent } from './assessment-mobile/assessment-mobile.component'
import {
  SPECIAL_TENANT_CATEGORIES_ORDER,
  computeCategoryScores,
  isHighlightEvaluation,
  isLowLightEvaluation,
  isSpecialTenant,
  isSpecialTenantWithoutCategories,
} from './assessment-evaluation.util'
import { findSpeakElementByHighlight } from './assessment-transcript.util'
import { MOBILE_BREAKPOINT } from '../../../util/breakpoints'
import {
  RetainedStableFields,
  applyRetainedStableFields,
  shouldSkipSummary,
  shouldSkipTranscript,
} from './assessment-field-retention.util'
import { AssessmentTagsComponent } from './assessment-tags/assessment-tags.component'

@UntilDestroy()
@Component({
  selector: 'app-assessment-home',
  standalone: true,
  imports: [
    ButtonComponent,
    MatIcon,
    MatSlideToggleModule,
    MatTooltip,
    SelectComponent,
    CommonModule,
    LoadingComponent,
    AvatarComponent,
    ContextMenuComponent,
    AiBadgeComponent,
    RouterModule,
    AssessmentMobileComponent,
    AssessmentTagsComponent,
    SpeakerLabelPipe,
    MarkdownPipe,
  ],
  templateUrl: './home.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit {
  @ViewChild('evaluation') evaluation?: ElementRef<HTMLDivElement>
  @ViewChild('transcript') transcript?: ElementRef<HTMLDivElement>
  @ViewChild('audioPlayer') audioPlayerRef?: ElementRef<HTMLAudioElement>

  selectAssessmentSubscription: Subscription | null = null
  assessments: Option[] = []
  selectedAssessment: Option = { id: 'none', name: '商談未設定' }

  user: User | null = null
  adminOrg: AdminOrg | null = null
  assessment: Assessment | null = null
  selectedKeyAction: KeyActionEvaluation | null = null
  keyActionEvaluations: KeyActionEvaluation[] = []
  /** カテゴリ別スコアのキャッシュ */
  categoryScores: { category: string; average: number }[] = []
  /** ハイライト判定キャッシュ */
  highlightMap: Map<string, boolean> = new Map()
  /** ロウライト判定キャッシュ */
  lowlightMap: Map<string, boolean> = new Map()
  // デフォルト値はBEで取得しているためここでは空文字を設定
  overallScoreLabel = ''
  speakerLabelValues: CustomLabelValueMap = {}

  skillMaps: SkillMap[] = []
  accounts: { name: string }[] = []

  showSummary = false
  collapsed = false
  isMobile = false

  downloadUrl: string | null = null

  /** ポーリングで全再取得・全再代入しても保持する確定フィールド(transcript / summary) */
  private retained: RetainedStableFields = {
    transcript: undefined,
    summary: null,
  }
  /** retained をリセットすべき assessment 切替(継続ポーリングは同一 id)を検知するための現在の id */
  private retainedAssessmentId: string | null = null

  contextMenu: ContextMenu[] = [
    {
      icon: 'delete',
      title: '削除',
      type: 'DELETE',
    },
  ]

  constructor(
    private matDialog: MatDialog,
    private snackBar: MatSnackBar,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private userStore: UserStore,
    private assessmentStore: AssessmentStore,
    private authService: AuthService,
    private messageDialogService: MessageDialogService,
    private storageService: StorageService,
    private cdr: ChangeDetectorRef,
    private titleService: Title,
    private csvExportService: CsvExportService,
    private customLabelService: CustomLabelService,
    private breakpointObserver: BreakpointObserver,
  ) {
    this.titleService.setTitle(getTitle('ホーム'))
  }

  async ngOnInit(): Promise<void> {
    this.breakpointObserver
      .observe([MOBILE_BREAKPOINT])
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.isMobile = result.matches
      })

    this.userStore.getAdminOrg().subscribe((adminOrg) => {
      this.adminOrg = adminOrg
      if (this.keyActionEvaluations.length > 0) {
        this.rebuildEvaluationCache()
      }
    })

    this.activatedRoute.queryParams
      .pipe(untilDestroyed(this))
      .subscribe(async () => {
        ;(
          await this.initializeHome(
            this.activatedRoute.snapshot.queryParamMap.get('assessment-id'),
            this.activatedRoute.snapshot.queryParamMap.get('key-action-id'),
          )
        ).subscribe()
      })
    this.assessmentStore.getSkillMaps().subscribe((skillMaps) => {
      this.skillMaps = skillMaps
    })
    this.assessmentStore.getAccounts().subscribe((accounts) => {
      this.accounts = accounts
    })
  }

  get highLights() {
    if (!this.adminOrg) return []
    return this.keyActionEvaluations
      .slice()
      .filter((e) => this.isHighlight(e))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
  }

  get lowLights() {
    if (!this.adminOrg) return []

    const filtered = this.keyActionEvaluations
      .slice()
      .filter((e) => this.isLowLight(e))
      .sort((a, b) => a.score - b.score)

    if (this.isSpecialTenantWithoutCategories()) {
      return filtered
    }

    // 通常テナントはTOP 3のみ
    return filtered.slice(0, 3)
  }

  isHighlight(evaluation: KeyActionEvaluation) {
    return this.highlightMap.get(evaluation.id) ?? false
  }

  isLowLight(evaluation: KeyActionEvaluation) {
    return this.lowlightMap.get(evaluation.id) ?? false
  }

  startUploadingFile() {
    if (
      this.matDialog.openDialogs.some(
        (dialog) => dialog.id === 'file-information-input',
      )
    ) {
      return
    }
    this.openFileInformationInput()
  }

  openFileInformationInput() {
    const dialogRef = this.matDialog.open(FileInformationInputComponent, {
      id: 'file-information-input',
      disableClose: true,
      panelClass: ['unset-mat-dialog-padding'],
      enterAnimationDuration: '0ms',
    })

    dialogRef.componentInstance.user = this.user ?? null
    dialogRef.componentInstance.skillMaps = this.skillMaps
    dialogRef.componentInstance.accounts = this.accounts
    dialogRef.componentInstance.userLicense = this.user?.license ?? null

    dialogRef.afterClosed().subscribe(async (assessmentId) => {
      if (!assessmentId) return

      this.assessment = null
      this.selectAssessmentSubscription?.unsubscribe()
      this.router.navigate(['/home'], {
        queryParams: { 'assessment-id': assessmentId },
        queryParamsHandling: 'merge',
      })
      this.assessmentStore.getAccounts().subscribe((accounts) => {
        this.accounts = accounts
      })
    })
  }

  isSelfUser(): boolean {
    return this.user?.id === this.authService.user?.id
  }

  isDirectManager(): boolean {
    return this.user?.reportToUserId === this.authService.user?.id
  }

  isEnablerOrAdmin(): boolean {
    return (
      this.userStore
        .ctxUser()
        ?.roles.some(
          (role) => role.code === 'ENABLER' || role.code === 'SUPER_ADMIN',
        ) ?? false
    )
  }

  hasLicense(): boolean {
    return this.user?.license != null
  }

  copyToClipboard(text: string | null) {
    if (!text) return
    navigator.clipboard.writeText(text)
    this.snackBar.open('コピーしました', '閉じる', {
      duration: 2000,
    })
  }

  showRenameBusinessMeeting() {
    const dialogRef = this.matDialog.open(RenameBusinessMeetingComponent, {
      id: 'rename-business-meeting',
      disableClose: true,
      panelClass: ['unset-mat-dialog-padding'],
      autoFocus: false,
    })

    dialogRef.componentInstance.data = {
      assessmentId: this.assessment?.id ?? '',
      name: this.assessment?.businessMeeting.title ?? '',
    }

    dialogRef.afterClosed().subscribe((newName) => {
      if (newName) {
        const newAssessments = this.assessments.map((assessment) => {
          if (assessment.id === this.assessment?.id) {
            return { id: assessment.id, name: newName }
          }
          return assessment
        })
        this.assessments = newAssessments
      }
    })
  }

  showRegisterInformation() {
    const dialogRef = this.matDialog.open(FileInformationViewComponent, {
      id: 'file-information-view',
      disableClose: true,
      panelClass: ['unset-mat-dialog-padding'],
      enterAnimationDuration: '0ms',
    })

    dialogRef.componentInstance.businessMeetingName =
      this.assessment?.businessMeeting?.title ?? null
    dialogRef.componentInstance.accountName =
      this.assessment?.businessMeeting?.account?.name ?? null
    dialogRef.componentInstance.skillMapName =
      this.assessment?.skillMapName ?? ''
    dialogRef.componentInstance.phaseName =
      this.assessment?.businessMeeting.applicablePhaseNames?.join(', ') ?? ''
    dialogRef.componentInstance.recordDate =
      this.assessment?.businessMeeting?.recordDate ?? null
    // 詳細に表示するファイル名は音声があれば音声パス、なければテキスト（文字起こし）パスから取る
    const uploadedFilePath =
      this.assessment?.businessMeeting?.audioFilePath ??
      this.assessment?.businessMeeting?.filePath
    const path = uploadedFilePath?.split('/')
    dialogRef.componentInstance.fileName = path
      ? (path[path.length - 1] ?? null)
      : null
    dialogRef.componentInstance.reference =
      this.assessment?.businessMeeting?.reference ?? null
    const durationSeconds = this.assessment?.durationSeconds ?? 0
    dialogRef.componentInstance.durationTIme = durationSeconds
      ? `${String(Math.floor(durationSeconds / 3600)).padStart(2, '0')}:${String(Math.floor((durationSeconds % 3600) / 60)).padStart(2, '0')}:${String(durationSeconds % 60).padStart(2, '0')}`
      : '00:00:00'
  }

  /**
   * 評価・サマリーの表示枠（AIバッジ付きパネル）を出すか。
   * skip でサマリー未生成の間は文字起こしのみを表示し、
   * 後からサマリーが追加され COMPLETED になったら表示する
   */
  get showEvaluationPanel(): boolean {
    if (!this.assessment) {
      return false
    }
    return (
      !this.assessment.skipEvaluation ||
      this.assessment.businessMeeting.summaryStatus === 'COMPLETED'
    )
  }

  toggleSummary() {
    this.showSummary = !this.showSummary
  }

  selectKeyAction(evaluationId?: string, keyActionId?: string) {
    if (
      keyActionId &&
      !this.assessment?.keyActionEvaluations.find(
        (e) => e.keyAction.id === keyActionId,
      )
    ) {
      return
    }
    this.showSummary = false
    if (evaluationId) {
      this.selectedKeyAction =
        this.assessment?.keyActionEvaluations.find(
          (e) => e.id === evaluationId,
        ) ?? null
    } else if (keyActionId) {
      this.selectedKeyAction =
        this.assessment?.keyActionEvaluations.find(
          (e) => e.keyAction.id === keyActionId,
        ) ?? null
    }
    this.cdr.detectChanges()

    setTimeout(() => {
      if (this.selectedKeyAction) {
        this.scrollEvaluationToTop()
        this.scrollToHighlight(this.selectedKeyAction.highlight)
      }
    })
  }

  changeAssessment(event: { id: string }) {
    if (!this.user) {
      return
    }

    if (event.id === this.assessment?.id) return

    this.assessment = null
    this.selectAssessmentSubscription?.unsubscribe()
    // 現在のクエリパラメータを取得してコピー
    const currentParams = { ...this.activatedRoute.snapshot.queryParams }

    // 除外したいキーを削除（例: 'key-action-id'）
    delete currentParams['key-action-id']

    // 新しいパラメータを追加・上書き
    const newParams = {
      ...currentParams,
      'assessment-id': event.id,
    }

    // ナビゲート
    this.router.navigate(['/home'], {
      queryParams: newParams,
    })
  }

  private selectAssessment(assessment: {
    id: string
    status: AssessmentStatus
  }) {
    this.downloadUrl = null
    this.overallScoreLabel = ''
    this.speakerLabelValues = {}

    // 別 assessment への切替時のみ保持値をリセットする。
    // summaryStatus=PENDING 時の継続ポーリングは同一 id で呼び直されるため、
    // ここではリセットされず transcript / summary の skip が維持される。
    if (assessment.id !== this.retainedAssessmentId) {
      this.retainedAssessmentId = assessment.id
      this.retained = { transcript: undefined, summary: null }
    }

    const timer$ =
      assessment.status === 'COMPLETED' || assessment.status === 'FAILED'
        ? of(0) // 初回取得後、summaryStatus が PENDING なら下の subscribe 内で継続ポーリングに切り替え
        : timer(0, 5000)

    return timer$.pipe(
      untilDestroyed(this),
      switchMap(() => {
        const skipTranscript = shouldSkipTranscript(this.retained)
        const skipSummary = shouldSkipSummary(this.retained)
        return this.assessmentStore
          .getAssessment(assessment.id, { skipTranscript, skipSummary })
          .pipe(
            tap((assessment) => {
              this.assessment = assessment
              // 取得済みの transcript / summary を保持し、@skip で外れた回は埋め戻す。
              // showSummary 判定・buildContextMenu より前に行い表示退行を防ぐ。
              this.retained = applyRetainedStableFields(
                assessment,
                this.retained,
              )

              // Assessment に紐づく SkillMap のカスタムラベルを取得
              // untilDestroyed: コンポーネント破棄中にレスポンスが返った場合の Angular 警告を防ぐ
              const requestedAssessmentId = assessment.id
              this.customLabelService
                .getCustomLabels(
                  [
                    LABEL_KEYS.OVERALL_SCORE,
                    LABEL_KEYS.SALES_PERSON,
                    LABEL_KEYS.CUSTOMER,
                  ],
                  assessment.skillMapId,
                )
                .pipe(untilDestroyed(this))
                .subscribe((labels) => {
                  if (this.assessment?.id !== requestedAssessmentId) return

                  const assessmentLabels = extractAssessmentLabels(labels)
                  this.overallScoreLabel = assessmentLabels.overallScoreLabel
                  this.speakerLabelValues = assessmentLabels.speakerLabelValues
                })

              const audioPath = this.assessment?.businessMeeting?.audioFilePath
              if (audioPath && isAudioFilePath(audioPath)) {
                this.storageService
                  .getDownloadUrl(audioPath)
                  .subscribe((url) => {
                    this.downloadUrl = url
                    setTimeout(() => {
                      this.audioPlayerRef?.nativeElement.load()
                    }, 200)
                  })
              }

              this.keyActionEvaluations = assessment.keyActionEvaluations
              this.categoryScores = computeCategoryScores(
                this.keyActionEvaluations,
                this.user?.orgId,
              )
              this.rebuildEvaluationCache()
              if (
                assessment.status === 'COMPLETED' ||
                assessment.status === 'FAILED'
              ) {
                this.cdr.detectChanges()
              }
              if (assessment.status === 'COMPLETED') {
                this.loadUser(this.user?.id ?? '')
              }
              if (
                assessment.businessMeeting.summary ||
                (assessment.businessMeeting.summaryStatus === 'PENDING' &&
                  !assessment.skipEvaluation)
              ) {
                this.showSummary = true
              } else {
                this.showSummary = false
                if (this.keyActionEvaluations.length > 0) {
                  this.selectedKeyAction = this.keyActionEvaluations[0]
                  setTimeout(() => {
                    if (this.selectedKeyAction) {
                      this.scrollToHighlight(this.selectedKeyAction.highlight)
                    }
                  })
                }
              }
              this.contextMenu = this.buildContextMenu()
            }),
          )
      }),
      catchError((error) => {
        if (
          CombinedGraphQLErrors.is(error) &&
          error.errors.some((e) => e.extensions?.['code'] === 'NOT_FOUND')
        ) {
          return EMPTY
        }
        throw error
      }),
    )
  }

  async initializeHome(
    assessmentId: string | null = null,
    keyActionId: string | null = null,
  ) {
    let userId = this.activatedRoute.snapshot.queryParamMap.get('user-id')
    if (!userId) {
      userId =
        (await lastValueFrom(this.userStore.getOneSelf({ useCache: true })))
          ?.id ?? null
    }
    if (!userId) {
      alert('authenticated user not found')
      throw new Error('authenticated user not found')
    }

    return this.userStore.getUser(userId).pipe(
      tap(async (user) => {
        this.user = user

        if (user.assessments.length === 0) {
          return of([])
        }

        this.assessments = user.assessments
          .sort((a, b) => b.assessDate.getTime() - a.assessDate.getTime())
          .map((assessment) => ({
            id: assessment.id,
            name: assessment.businessMeeting.title,
          }))

        let selectedAssessment = this.user.assessments.find(
          (assessment) => assessment.id === assessmentId,
        )
        let selectedAssessmentOption = this.assessments.find(
          (assessment) => assessment.id === assessmentId,
        )
        if (!assessmentId) {
          selectedAssessment = this.user.assessments[0]
          selectedAssessmentOption = this.assessments[0]
        } else if (!selectedAssessment) {
          selectedAssessment = await lastValueFrom(
            this.assessmentStore.getAssessment(assessmentId),
          )
          selectedAssessmentOption = {
            id: selectedAssessment.id,
            name: selectedAssessment.businessMeeting.title,
          }
        }

        this.selectedAssessment = selectedAssessmentOption ?? {
          id: 'none',
          name: '商談未設定',
        }

        this.titleService.setTitle(
          getTitle(
            `アセスメント（${this.user.name} | ${this.selectedAssessment.name}）`,
          ),
        )

        this.selectAssessmentSubscription = this.selectAssessment(
          selectedAssessment,
        ).subscribe((assessment) => {
          const isTerminalStatus =
            assessment.status === 'COMPLETED' || assessment.status === 'FAILED'
          // skip はサマリーを生成しないため PENDING のままでもポーリングしない
          const isSummaryPending =
            assessment.businessMeeting.summaryStatus === 'PENDING' &&
            !assessment.skipEvaluation

          if (isTerminalStatus && isSummaryPending) {
            // 評価完了だが要約生成中の場合、ポーリングを継続
            this.selectAssessmentSubscription?.unsubscribe()
            this.selectAssessmentSubscription = this.selectAssessment({
              ...selectedAssessment,
              status: 'IN_PROGRESS' as AssessmentStatus,
            }).subscribe((a) => {
              if (a.businessMeeting.summaryStatus !== 'PENDING') {
                this.selectAssessmentSubscription?.unsubscribe()
              }
            })
            return
          }
          if (isTerminalStatus) {
            this.selectAssessmentSubscription?.unsubscribe()
          }
          if (keyActionId) {
            this.selectKeyAction('', keyActionId)
          }
        })

        return
      }),
    )
  }

  async markContentAsAccessed(recommendedContentId: string) {
    if (!this.user || this.user.id !== this.authService.user?.id) {
      return
    }
    this.assessmentStore
      .markContentAsAccessed(recommendedContentId)
      .pipe(
        tap(() => {
          const content = this.selectedKeyAction?.recommendedContents.find(
            (c) => c.id === recommendedContentId,
          )
          if (content) {
            content.isAccessed = true
          }
        }),
      )
      .subscribe()
  }

  isNotSet() {
    return this.selectedAssessment.id === 'none'
  }

  deleteAssessment(assessment: Assessment) {
    this.messageDialogService
      .showConfirm(
        `
      「${assessment.businessMeeting.title}」のアセスメントを削除してもよろしいですか？
      この操作は取り消すことができません。
      `,
        {
          primaryButtonText: '削除',
          secondaryButtonText: 'キャンセル',
        },
      )
      .pipe(
        filter((result) => result === DialogResult.PrimaryButtonClicked),
        switchMap(() => {
          return this.assessmentStore.deleteAssessment(assessment.id).pipe(
            tap(() => {
              this.assessment = null
              this.selectedAssessment = { id: 'none', name: '商談未設定' }
              this.selectAssessmentSubscription?.unsubscribe()
            }),
            catchError((error) => {
              captureException(error)
              this.messageDialogService
                .showError(
                  'アセスメントの削除に失敗しました。時間をおいて再度お試しください。',
                )
                .subscribe()
              return of()
            }),
          )
        }),
        tap(async () => {
          // remove assessment id from query parameter
          await this.router.navigate(['/home'], {
            queryParams: {
              'user-id': this.user?.id,
            },
            replaceUrl: true,
          })
        }),
        switchMap(() => {
          // refresh assessment list and select default assessment
          return this.initializeHome()
        }),
      )
      .subscribe()
  }

  isMediaFile() {
    return isAudioFilePath(this.assessment?.businessMeeting?.audioFilePath)
  }

  getRoleModelAssessmentTitle(assessment: RoleModelAssessment): string {
    const keyActionAlias = this.selectedKeyAction?.keyAction.alias || 'KA00'
    const userName = assessment.user.name
    return `${keyActionAlias}が得意な${userName}さんの商談`
  }

  seekTo(speakedAt: string) {
    if (this.audioPlayerRef) {
      const timeInSeconds = this.convertToSeconds(speakedAt)
      const audioPlayer = this.audioPlayerRef.nativeElement
      audioPlayer.onseeked = () => {
        audioPlayer.play()
        audioPlayer.onseeked = null
      }
      audioPlayer.currentTime = timeInSeconds
    }
  }

  getMimeType(url: string) {
    const extension = url.split('?').shift()?.split('.')?.pop()
    if (!extension) {
      return 'audio/mpeg'
    }
    return getMimeType(extension)
  }

  contextMenuClick(event: string) {
    switch (event) {
      case 'DOWNLOAD':
        this.exportTimeSeriesEvaluations()
        break
      case 'TRANSCRIPT_DOWNLOAD':
        this.exportTranscriptText()
        break
      case 'RENAME':
        this.showRenameBusinessMeeting()
        break
      case 'DELETE':
        if (this.assessment) {
          this.deleteAssessment(this.assessment)
        }
        break
    }
  }

  hasTimeSeriesEvaluations() {
    return this.assessment?.keyActionEvaluations.some(
      (evaluation) => evaluation.timeSeries.length > 0,
    )
  }

  private buildContextMenu(): ContextMenu[] {
    const items: ContextMenu[] = []
    if (this.hasTimeSeriesEvaluations()) {
      items.push({
        icon: 'download',
        title: '時間帯別評価ダウンロード',
        type: 'DOWNLOAD',
      })
    }
    if (canDownloadTranscript(this.assessment)) {
      items.push({
        icon: 'description',
        title: '文字起こしダウンロード',
        type: 'TRANSCRIPT_DOWNLOAD',
      })
    }
    items.push({ icon: 'edit', title: '名前を変更', type: 'RENAME' })
    items.push({ icon: 'delete', title: '削除', type: 'DELETE' })
    return items
  }

  exportTranscriptText(): void {
    if (!this.assessment || !this.user) return
    if (!canDownloadTranscript(this.assessment)) return

    const transcript = this.assessment.businessMeeting.transcript ?? []
    const text = formatTranscriptAsText(transcript, {
      speakerLabelValues: this.speakerLabelValues,
    })
    if (!text) return

    const filename = buildTranscriptFilename(
      this.assessment.businessMeeting.title,
      this.user.name,
      new Date(),
    )
    downloadBlob(
      new Blob([text], { type: 'text/plain;charset=utf-8' }),
      filename,
    )
  }

  exportTimeSeriesEvaluations() {
    if (!this.assessment) {
      return
    }
    const data = this.assessment.keyActionEvaluations.flatMap((evaluation) => {
      return evaluation.timeSeries.map((ts, index) => {
        return {
          keyActionName: evaluation.keyAction.name,
          calculationMethod:
            evaluation.keyAction.assessmentQuestionType === 'SCALE'
              ? '最大'
              : '平均',
          timeSeriesIndex: index + 1,
          fromTime: ts.fromTime,
          toTime: ts.toTime,
          score: ts.score,
          reason: ts.reason,
          strength: ts.strength,
          improvement: ts.improvement,
          quote: ts.quote,
        }
      })
    })
    this.csvExportService.downloadCsv(
      this.assessment?.businessMeeting.title + '_時間帯別評価.csv',
      [
        { label: 'キーアクション', key: 'keyActionName' },
        { label: '時間帯番号', key: 'timeSeriesIndex' },
        { label: '開始', key: 'fromTime' },
        { label: '終了', key: 'toTime' },
        { label: 'スコア', key: 'score' },
        { label: '理由', key: 'reason' },
        { label: '良かった点', key: 'strength' },
        { label: '改善したい点', key: 'improvement' },
        { label: '参照箇所', key: 'quote' },
        { label: '評価算出方法', key: 'calculationMethod' },
      ],
      data,
    )
  }

  // 個社カスタマイズ用メソッド
  isSpecialTenant(): boolean {
    return isSpecialTenant(this.user?.orgId)
  }

  // 個社カスタマイズ用メソッド: スペシャルテナントかつKAカテゴリがある場合
  isSpecialTenantWithCategories(): boolean {
    return this.isSpecialTenant() && this.categoryScores.length > 0
  }

  // 個社カスタマイズ用メソッド: スペシャルテナントかつKAカテゴリがない場合
  isSpecialTenantWithoutCategories(): boolean {
    return isSpecialTenantWithoutCategories(
      this.user?.orgId,
      this.categoryScores.length,
    )
  }

  // 個社カスタマイズ用メソッド
  getCategoryImprovements(): [string, KeyActionEvaluation[]][] {
    // 全カテゴリを元の順序で取得 (nullを除外)
    const allCategories: string[] = Array.from(
      new Set(
        this.keyActionEvaluations.flatMap(
          (e) => e.keyAction.categories?.filter((c): c is string => !!c) || [],
        ),
      ),
    )

    const categoryMap = new Map<string, KeyActionEvaluation[]>()

    // まず全てのカテゴリを初期化
    allCategories.forEach((category) => {
      categoryMap.set(category, [])
    })

    // 改善点をカテゴリごとに分類 (全件からフィルタリングし、orderNoでソート)
    this.keyActionEvaluations
      .filter((e) => this.isLowLight(e))
      .sort((a, b) => {
        if (a.score !== b.score) {
          return a.score - b.score // スコアが低い順
        }
        return a.keyAction.orderNo - b.keyAction.orderNo // スコアが同じ場合はorderNoが小さい順
      })
      .forEach((evaluation) => {
        if (!evaluation.keyAction.categories) return
        evaluation.keyAction.categories.forEach((category) => {
          if (category && categoryMap.has(category)) {
            categoryMap.get(category)!.push(evaluation)
          }
        })
      })

    // 元のカテゴリ順序で返す
    const results = allCategories.map((category) => [
      category,
      categoryMap.get(category)?.slice(0, 3) || [],
    ]) as [string, KeyActionEvaluation[]][]

    const orderedCategories = SPECIAL_TENANT_CATEGORIES_ORDER
    return results.sort(([a], [b]) => {
      const aIndex = orderedCategories.indexOf(a)
      const bIndex = orderedCategories.indexOf(b)

      // 指定順序にあるものはその順番でソート
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
      // 片方だけ指定順序にある場合は、指定順序のものを先に
      if (aIndex !== -1) return -1
      if (bIndex !== -1) return 1
      // どちらも指定順序にない場合は元の順序を保持
      return 0
    })
  }

  private convertToSeconds(timeString: string): number {
    const parts = timeString.split(':').map((part) => parseInt(part, 10))

    let seconds = 0

    if (parts.length === 3) {
      const [hours, minutes, secs] = parts
      seconds = hours * 3600 + minutes * 60 + secs
    } else if (parts.length === 2) {
      const [minutes, secs] = parts
      seconds = minutes * 60 + secs
    }

    return seconds
  }

  /** ハイライト/ロウライトキャッシュを再構築する */
  private rebuildEvaluationCache(): void {
    const threshold = this.adminOrg?.scoreImprovementThreshold ?? 3
    const specialWithout = this.isSpecialTenantWithoutCategories()

    this.highlightMap = new Map()
    this.lowlightMap = new Map()

    for (const evaluation of this.keyActionEvaluations) {
      this.highlightMap.set(
        evaluation.id,
        isHighlightEvaluation(evaluation, threshold, specialWithout),
      )
      this.lowlightMap.set(
        evaluation.id,
        isLowLightEvaluation(evaluation, threshold, specialWithout),
      )
    }
  }

  private scrollEvaluationToTop() {
    if (!this.evaluation) return
    this.evaluation.nativeElement.scrollTo(0, 0)
  }

  private scrollToHighlight(highlight: string) {
    if (!this.transcript) return
    const element = findSpeakElementByHighlight(
      this.transcript.nativeElement,
      highlight,
    )
    if (!element) {
      console.warn(
        `scrollToHighlight: highlight "${highlight}" に一致する発話要素が見つかりません`,
      )
      return
    }
    element.scrollIntoView({ behavior: 'smooth' })
  }

  private loadUser(userId: string) {
    this.userStore.getUser(userId).subscribe((user) => {
      this.user = user
    })
  }
}
